import { useEffect, useMemo } from "react";
import { useKeyboardActivation } from "../../../../hooks/useKeyboardActivation";
import { useSettings } from "../../hooks/useSettings";
import { SettingsRow } from "../settings-row/SettingsRow";
import "./ZoomingSensitivity.css";

const SLIDER_MIN = 0;
const SLIDER_MAX = 100;
const ZOOM_MIN = 0.1;
const ZOOM_MAX = 2.0;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const zoomToSliderValue = zoomSensitivity => {
    const normalizedZoom = clamp(Number(zoomSensitivity) || 1, ZOOM_MIN, ZOOM_MAX);
    const ratio = (normalizedZoom - ZOOM_MIN) / (ZOOM_MAX - ZOOM_MIN);
    return Math.round(SLIDER_MIN + ratio * (SLIDER_MAX - SLIDER_MIN));
};

const sliderToZoomValue = sliderValue => {
    const normalizedSlider = clamp(Number(sliderValue) || 0, SLIDER_MIN, SLIDER_MAX);
    const ratio = (normalizedSlider - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN);
    return Number((ZOOM_MIN + ratio * (ZOOM_MAX - ZOOM_MIN)).toFixed(2));
};

export const ZoomingSensitivity = () => {
    const { floatSensitivity, updateFloatSensitivity, preferencesLoading } =
        useSettings();

    const zoomValue = useMemo(
        () => zoomToSliderValue(floatSensitivity),
        [floatSensitivity]
    );

    useEffect(() => {
        if (!Number.isFinite(floatSensitivity) || floatSensitivity <= 0) {
            updateFloatSensitivity(1.0);
        }
    }, [floatSensitivity, updateFloatSensitivity]);

    const applySliderValue = sliderValue => {
        updateFloatSensitivity(sliderToZoomValue(sliderValue));
    };

    const handleZoomChange = e => {
        applySliderValue(Number(e.target.value));
    };

    const handleDecrease = () => {
        applySliderValue(Math.max(SLIDER_MIN, zoomValue - 5));
    };

    const handleIncrease = () => {
        applySliderValue(Math.min(SLIDER_MAX, zoomValue + 5));
    };

    const handleDecreaseKeyDown = useKeyboardActivation(handleDecrease);
    const handleIncreaseKeyDown = useKeyboardActivation(handleIncrease);

    return (
        <SettingsRow labelKey="286" className="settings-zooming-sensitivity">
            <div className="zooming-sensitivity-container">
                <button 
                    id="zoom-arrow-left-button"
                    className="zoom-arrow-button zoom-arrow-left"
                    onClick={handleDecrease}
                    onKeyDown={handleDecreaseKeyDown}   
                    type="button"
                    disabled={preferencesLoading}
                >
                    ‹
                </button>
                <input
                    type="range"
                    min={SLIDER_MIN}
                    max={SLIDER_MAX}
                    step={1}
                    value={zoomValue}
                    onChange={handleZoomChange}
                    className="zoom-slider"
                    aria-label="Zoom sensitivity"
                    aria-valuemin={SLIDER_MIN}
                    aria-valuemax={SLIDER_MAX}
                    aria-valuenow={zoomValue}
                    disabled={preferencesLoading}
                />
                <button 
                    id="zoom-arrow-right-button"
                    className="zoom-arrow-button zoom-arrow-right"
                    onClick={handleIncrease}
                    onKeyDown={handleIncreaseKeyDown}
                    aria-label="Zoom sensitivity increase"
                    type="button"
                    disabled={preferencesLoading}
                >
                    ›
                </button>
            </div>
        </SettingsRow>
    )
}
